import React, { useState } from 'react';
import { User, Lock, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { GhostLogo } from './GhostLogo';

interface RegisterProps {
    onGoToLogin: () => void;
}

export function Register({ onGoToLogin }: RegisterProps) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/admin/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
            } else {
                setError(data.message || 'Registration failed');
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-cyber-dark cyber-grid relative overflow-hidden flex items-center justify-center p-6">
            {/* Background effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-20 left-[15%] w-2 h-2 rounded-full bg-cyan-400/40 animate-node-pulse" />
                <div className="absolute top-40 right-[20%] w-3 h-3 rounded-full bg-cyan-400/30 animate-node-pulse-delayed" />
                <div className="absolute bottom-[30%] left-[25%] w-2 h-2 rounded-full bg-cyan-400/20 animate-node-pulse" />
                <div className="absolute top-0 left-1/3 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-cyan-500/3 rounded-full blur-3xl" />
            </div>

            <div className="w-full max-w-md relative z-10">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center animate-cyber-glow">
                            <GhostLogo size={36} variant="outline" />
                        </div>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
                    <p className="text-gray-500">Register for MessengerPhantom</p>
                </div>

                {success ? (
                    <div className="glass-card rounded-2xl p-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto mb-4 animate-cyber-glow">
                            <CheckCircle2 className="w-8 h-8 text-cyan-400" />
                        </div>
                        <h2 className="text-xl font-bold text-white mb-2">Registration Successful!</h2>
                        <p className="text-gray-400 mb-6">
                            Your account is pending admin approval. You'll be able to sign in once an admin approves your account.
                        </p>
                        <button
                            onClick={onGoToLogin}
                            className="px-6 py-3 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 font-medium rounded-xl transition-all w-full"
                        >
                            Back to Sign In
                        </button>
                    </div>
                ) : (
                    <div className="glass-card rounded-2xl p-8">
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                                    <p className="text-red-300 text-sm">{error}</p>
                                </div>
                            )}

                            <div>
                                <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                    <User className="w-4 h-4" />
                                    Username
                                </label>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/40 focus:shadow-[0_0_15px_rgba(0,212,255,0.1)] transition-all"
                                    placeholder="Choose a username"
                                    required
                                    minLength={3}
                                    maxLength={30}
                                    pattern="[a-zA-Z0-9_]+"
                                    title="Letters, numbers, and underscores only"
                                />
                            </div>

                            <div>
                                <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                    <Lock className="w-4 h-4" />
                                    Password
                                </label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/40 focus:shadow-[0_0_15px_rgba(0,212,255,0.1)] transition-all"
                                    placeholder="Create a password"
                                    required
                                    minLength={6}
                                />
                            </div>

                            <div>
                                <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                    <Lock className="w-4 h-4" />
                                    Confirm Password
                                </label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/40 focus:shadow-[0_0_15px_rgba(0,212,255,0.1)] transition-all"
                                    placeholder="Confirm your password"
                                    required
                                    minLength={6}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-black font-semibold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_20px_rgba(0,212,255,0.3)]"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Creating account...
                                    </>
                                ) : (
                                    'Create Account'
                                )}
                            </button>
                        </form>
                    </div>
                )}

                <p className="text-center text-gray-600 text-sm mt-6">
                    Already have an account?{' '}
                    <button onClick={onGoToLogin} className="text-cyan-400 hover:text-cyan-300 transition-colors">
                        Sign In
                    </button>
                </p>
            </div>
        </div>
    );
}
